<template>
  <div
    class="sp-interactive-card-wrapper"
    tabindex="0"
    role="button"
    :aria-expanded="ariaExpanded"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <sp-card class="sp-interactive-card" :class="classModifiers" part="base">
      <div slot="title" class="sp-interactive-card__header">
        <div class="sp-interactive-card__title-group">
          <sp-card-title class="sp-interactive-card__title">
            <slot name="title">
              {{ title }}
            </slot>
          </sp-card-title>
          <sp-card-subtitle v-if="subtitle" class="sp-interactive-card__subtitle">
            {{ subtitle }}
          </sp-card-subtitle>
        </div>
        <div class="sp-interactive-card__activator">
          <transition name="fade" mode="out-in">
            <sp-icon :key="iconName" :name="iconName"></sp-icon>
          </transition>
        </div>
      </div>

      <transition name="expand">
        <div v-if="isEffectiveExpanded">
          <sp-card-content class="sp-interactive-card__content">
            <slot />
          </sp-card-content>
        </div>
      </transition>
    </sp-card>

    <sp-modal
      :modal-title="title"
      :title-oneline="false"
      :open="isModalOpen"
      :width="`var(--modal-width)`"
      :class="modalClassModifiers"
      @update-open="handleModalUpdate"
      @close="handleModalClose"
    >
      <div v-if="subtitle" slot="subtitle" class="modal-subtitle">{{ subtitle }}</div>

      <div class="modal-content-wrapper">
        <slot />
      </div>
    </sp-modal>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, useHost } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import { INTERACTION_MODES, isInteractionMode, props as propsDefinitions } from "./props/card.js";

const props = defineProps(propsDefinitions);

const { breakpointModifiers } = useBreakpoints();

const interactionMode = ref(props.interactionMode);
const isExpanded = ref(toBoolean(props.expanded));
const isModalOpen = ref(false);

const { exposeMethods } = useExpose();
const host = useHost();

exposeMethods({
  expand: () => (isExpanded.value = true),
  collapse: () => (isExpanded.value = false),
  toggle: () => (isExpanded.value = !isExpanded.value),
  getInteractionMode: () => interactionMode.value,
  setInteractionMode,
});

function expandIfHashMatches() {
  const hostId = host?.id;
  if (!hostId) {
    return;
  }

  const hash = window.location.hash.replace(/^#/, "");
  if (hash === hostId) {
    isExpanded.value = true;

    // The browser's native hash scroll fires before custom elements render,
    // so the scroll position is often inaccurate due to layout shifts. It must
    // be 100ms or more to ensure the element is fully rendered and the scroll
    // position is correct.
    setTimeout(() => {
      host?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 100);
  }
}

onMounted(() => {
  if (host?.id) {
    expandIfHashMatches();
    window.addEventListener("hashchange", expandIfHashMatches);
  }
});

onUnmounted(() => {
  window.removeEventListener("hashchange", expandIfHashMatches);
});

const isInteractionModeExpand = computed(() => interactionMode.value === INTERACTION_MODES.expand);
const isInteractionModeModal = computed(() => interactionMode.value === INTERACTION_MODES.modal);

const isEffectiveExpanded = computed(() => isInteractionModeExpand.value && isExpanded.value);

const ariaExpanded = computed(() => (isInteractionModeExpand.value ? isExpanded.value.toString() : undefined));

const iconName = computed(() => (isEffectiveExpanded.value ? "subtract-filled" : "add-filled"));

const classModifiers = computed(() => [
  ...breakpointModifiers.value,
  {
    "--is-expanded": isExpanded.value,
    [`--interaction-mode-${interactionMode.value}`]: interactionMode.value,
  },
]);

const modalClassModifiers = computed(() => [...breakpointModifiers.value]);

function setInteractionMode(mode) {
  if (isInteractionMode(mode)) {
    interactionMode.value = mode;
  }
}

function toggleExpandMode() {
  isExpanded.value = !isExpanded.value;
}

function openModal() {
  isModalOpen.value = true;
}

function applyInteractionMode() {
  if (isInteractionModeExpand.value) {
    toggleExpandMode();
  } else if (isInteractionModeModal.value) {
    openModal();
  }
}

function handleClick(event) {
  if (event.target.closest("a")) {
    return;
  }

  applyInteractionMode();
}

function handleModalUpdate(event) {
  isModalOpen.value = event.detail?.[0];
}

function handleModalClose() {
  isModalOpen.value = false;
}
</script>

<style lang="scss">
@import "./shared/interactive-theme";

:host {
  /* Layout and sizing */
  --card-min-height-desktop: var(--sp-comp-interactive-card-min-height-desktop, 7.5rem);
  --card-min-height-mobile: var(--sp-comp-interactive-card-min-height-mobile, 5.5rem);
  --background: var(--sp-comp-interactive-card-background, var(--sp-sys-color-surface));
  --border-radius: var(--sp-comp-interactive-card-border-radius, var(--sp-ref-border-radius-lg));
  --border: var(--sp-comp-interactive-card-border-width, 1px) var(--sp-comp-interactive-card-border-style, solid)
    var(--sp-comp-interactive-card-border-color, var(--sp-sys-color-white));
  --padding-block: var(--sp-comp-interactive-card-padding-block, var(--sp-ref-scale-16));
  --padding-inline: var(--sp-comp-interactive-card-padding-inline, var(--sp-ref-scale-16));
  --box-shadow: var(--sp-comp-interactive-card-box-shadow, var(--sp-sys-elevation-3));

  /* Header */
  --header-gap: var(--sp-comp-interactive-card-header-gap, var(--sp-ref-scale-16));

  /* Title */
  --title-color: var(--sp-comp-interactive-card-title-color, var(--sp-sys-color-primary));
  --title-font-family: var(--sp-comp-interactive-card-title-font-family, var(--sp-sys-typescale-body-font-family));
  --title-font-size: var(--sp-comp-interactive-card-title-font-size, 1.25rem);
  --title-font-weight: var(--sp-comp-interactive-card-title-font-weight, var(--sp-sys-font-weight-bold));
  --title-line-height: var(--sp-comp-interactive-card-title-line-height, 1.75rem);

  /* Subtitle */
  --subtitle-color: var(--sp-comp-interactive-card-subtitle-color, var(--sp-sys-color-on-surface));
  --subtitle-font-family: var(
    --sp-comp-interactive-card-subtitle-font-family,
    var(--sp-sys-typescale-body-font-family)
  );
  --subtitle-font-size: var(--sp-comp-interactive-card-subtitle-font-size, 0.875rem);
  --subtitle-font-weight: var(--sp-comp-interactive-card-subtitle-font-weight, var(--sp-sys-font-weight-normal));
  --subtitle-line-height: var(--sp-comp-interactive-card-subtitle-line-height, 1.375rem);

  /* Content */
  --content-padding-top: var(--sp-comp-interactive-card-content-padding-top, var(--sp-ref-scale-16));
  --content-font-size: var(--sp-comp-interactive-card-content-font-size, var(--sp-sys-body-font-size));
  --content-line-height: var(--sp-comp-interactive-card-content-line-height, var(--sp-sys-body-line-height));
  --content-column-count: var(--sp-comp-interactive-card-content-column-count, 1);
  --content-column-count-sm: var(--sp-comp-interactive-card-content-column-count-sm, 1);
  --content-column-gap: var(--sp-comp-interactive-card-content-column-gap, var(--sp-ref-scale-16));

  /* Icon */
  --icon-size: var(--sp-comp-interactive-card-icon-size, 1.75rem);
  --icon-fill: var(--sp-comp-interactive-card-icon-fill, var(--sp-sys-color-outline));

  /* Modal */
  --modal-content-max-height: var(--sp-comp-interactive-card-modal-content-max-height, 70vh);

  display: block;
}

:host[data-interaction-mode="expand"] {
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-interactive-card-wrapper {
  border-radius: var(--border-radius);
}

.sp-interactive-card {
  background: var(--background);
  border: var(--border);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding-block: var(--padding-block);
  padding-inline: var(--padding-inline);
  cursor: pointer;

  &:focus {
    border-color: var(--sp-sys-color-primary);
    outline: none;
  }

  &.--interaction-mode-modal {
    min-height: var(--card-min-height-desktop);

    &.--is-sm-screen {
      min-height: var(--card-min-height-mobile);
    }
  }
}

.sp-interactive-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--header-gap);
}

.sp-interactive-card__title-group {
  flex: 1;
  min-width: 0;
}

.sp-interactive-card__title {
  color: var(--title-color);
  font-family: var(--title-font-family);
  font-size: var(--title-font-size);
  font-weight: var(--title-font-weight);
  line-height: var(--title-line-height);
}

.sp-interactive-card__subtitle {
  color: var(--subtitle-color);
  font-family: var(--subtitle-font-family);
  font-size: var(--subtitle-font-size);
  font-weight: var(--subtitle-font-weight);
  line-height: var(--subtitle-line-height);
  margin-top: var(--sp-interactive-card-subtitle-margin-top, var(--sp-ref-scale-4));
}

.sp-interactive-card__content {
  padding-top: var(--content-padding-top);
  font-size: var(--content-font-size);
  line-height: var(--content-line-height);
  column-count: var(--content-column-count);
  column-gap: var(--content-column-gap);
}

.sp-interactive-card.--is-sm-screen .sp-interactive-card__content {
  column-count: var(--content-column-count-sm);
}

.sp-interactive-card__activator {
  flex-shrink: 0;
  align-self: flex-start;

  sp-icon {
    --size: var(--icon-size);
    --fill: var(--icon-fill);
  }
}

.modal-content-wrapper {
  max-height: var(--modal-content-max-height);
  padding-bottom: var(--sp-ref-scale-8) !important;
}

sp-modal {
  &.--is-sm-screen {
    .modal-content-wrapper {
      max-height: none;
      overflow-y: auto !important;
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
  overflow: hidden;
  max-height: var(--sp-comp-interactive-card-expand-max-height, 1000px);
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--sp-ref-transition-fade-duration) var(--sp-ref-transition-fade-easing);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
